import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaPaperclip } from 'react-icons/fa';
import clarificationService from '../service/clarificationService.mjs';
import ClarificationThread from './ClarificationThread.mjs';
import {
  STATUS_LABELS, STATUS_PILL, formatWhen, announceChange,
  FILE_ACCEPT, MAX_FILES, MAX_MESSAGE_LENGTH, fileProblems,
} from '../util/clarificationText.mjs';

/**
 * The questions about one item, and the form to ask another.
 *
 *   claim   claimType + claimId - that claim's questions
 *   report  reportId            - questions about the report AND its claims,
 *                                 with the form asking about the report itself
 *
 * The one who approves a stage asks the stage below: an admin asks the
 * claimant, the auditor asks the admins, and the approvers above ask the
 * admins, the auditor once they have audited it and the regional director
 * once they have signed. The server works that out - the To list shows
 * exactly what it offers, each group with the names of everyone it reaches,
 * and a reader who cannot ask is told why.
 */
const RecipientNames = ({ id, recipients = [] }) => {
  if (!recipients.length) {
    return <span id={id} className="block text-xs text-amber-700">Nobody holds this role right now.</span>;
  }
  return (
    <span id={id} className="block text-xs text-gray-500 break-words">
      Goes to {recipients.map((r) => r.name).join(', ')}
    </span>
  );
};

const AskForm = ({ subject, onAsked, onCancel }) => {
  const [options, setOptions] = useState(null);
  const [reason, setReason] = useState(null);
  const [directedTo, setDirectedTo] = useState('admin');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => {
    let cancelled = false;
    clarificationService.askOptions(subject)
      .then((res) => {
        if (cancelled) return;
        if (!res?.canAsk) { setReason(res?.reason || 'You cannot ask a question about this.'); return; }
        setOptions(res.options || []);
        setDirectedTo(res.options?.[0]?.value || 'admin');
      })
      .catch((err) => { if (!cancelled) setReason(err.message); });
    return () => { cancelled = true; };
  }, [subject]);

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list || [])];
    const problem = fileProblems(next);
    setError(problem);
    if (!problem) setFiles(next);
    if (fileInput.current) fileInput.current.value = '';
  };

  const submit = async () => {
    const text = body.trim();
    if (!text) { setError('Type your question.'); return; }
    const problem = fileProblems(files);
    if (problem) { setError(problem); return; }
    setSending(true);
    setError(null);
    try {
      const created = await clarificationService.ask({ ...subject, directedTo, body: text, files });
      announceChange();
      onAsked(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (reason) {
    return (
      <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
        {reason}
        <button type="button" onClick={onCancel} className="ml-2 underline">Close</button>
      </div>
    );
  }
  if (!options) {
    return <p className="p-3 text-sm text-gray-500" role="status">Checking who can be asked…</p>;
  }

  return (
    <div className="p-3 border border-gray-200 rounded-lg bg-white space-y-3">
      <fieldset>
        <legend className="text-sm font-medium text-gray-800 mb-1">To</legend>
        <div className="flex flex-col gap-1.5">
          {options.map((o) => {
            const id = `ask-to-${o.value}`;
            // The names describe the option rather than name it, so a screen
            // reader hears "The East-Africa admins" and then who that is.
            return (
              <div key={`${o.value}-${o.userId || 'group'}`} className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  id={id}
                  type="radio"
                  name="ask-to"
                  value={o.value}
                  checked={directedTo === o.value}
                  onChange={() => setDirectedTo(o.value)}
                  aria-describedby={`${id}-who`}
                  className="mt-1 accent-[#8C1515]"
                />
                <div className="min-w-0">
                  <label htmlFor={id} className="block cursor-pointer">
                    {o.label.charAt(0).toUpperCase() + o.label.slice(1)}
                  </label>
                  <RecipientNames id={`${id}-who`} recipients={o.recipients} />
                </div>
              </div>
            );
          })}
        </div>
        {options.length === 1 && options[0].value === 'admin' && (
          <p className="mt-1 text-xs text-gray-500">
            The auditor and the regional director can be asked once they have signed this off.
          </p>
        )}
      </fieldset>

      <div>
        <label htmlFor="ask-body" className="block text-sm font-medium text-gray-800 mb-1">Your question</label>
        <textarea
          id="ask-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          autoFocus
          placeholder="What do you need explained?"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stanford-red"
        />
        <p className={`text-xs text-right tabular-nums ${body.length > MAX_MESSAGE_LENGTH ? 'text-red-700' : 'text-gray-400'}`}>
          {body.length}/{MAX_MESSAGE_LENGTH}
        </p>
      </div>

      {files.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Files to send">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 rounded">
              <span className="truncate max-w-[180px]">{f.name}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}>
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p role="alert" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</p>}

      <p className="text-xs text-gray-500">
        Nothing is sent back: the item stays where it is while you wait for the answer. You can still approve or
        send it back at any time{directedTo === 'claimant' ? '' : ' - approving with a question open asks you to confirm'}.
        Everyone who has already approved it is told the question was asked.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <input
            ref={fileInput}
            id="ask-files"
            type="file"
            multiple
            accept={FILE_ACCEPT}
            onChange={(e) => addFiles(e.target.files)}
            className="hidden"
          />
          <label
            htmlFor="ask-files"
            className={`min-h-[44px] sm:min-h-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer ${files.length >= MAX_FILES ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <FaPaperclip aria-hidden="true" /> Attach
          </label>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={sending || !body.trim() || body.length > MAX_MESSAGE_LENGTH}
            className="min-h-[44px] sm:min-h-0 px-4 py-1.5 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send question'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ClarificationPanel = ({ claimType, claimId, reportId, canAsk = true, onChange, emptyText }) => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(false);
  const [openId, setOpenId] = useState(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Memoised on the ids only, so a parent re-render does not refetch.
  const isClaim = !!claimId;
  const subjectKey = isClaim ? `claim:${claimType}:${claimId}` : `report:${reportId}`;
  const subject = React.useMemo(
    () => (isClaim ? { claimType, claimId } : { reportId }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subjectKey]
  );

  const load = useCallback(async () => {
    try {
      const rows = await clarificationService.list({ ...subject, status: 'any' });
      setThreads(rows);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [subject]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const changed = () => { load(); onChangeRef.current?.(); };

  return (
    <div className="space-y-3">
      {canAsk && !asking && (
        <button
          type="button"
          onClick={() => setAsking(true)}
          className="min-h-[44px] sm:min-h-0 w-full sm:w-auto px-4 py-2 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red"
        >
          ? {isClaim ? 'Ask a question about this claim' : 'Ask a question about this report'}
        </button>
      )}
      {asking && (
        <AskForm
          subject={subject}
          onCancel={() => setAsking(false)}
          onAsked={(created) => {
            setAsking(false);
            setOpenId(created?.thread?.id || null);
            changed();
          }}
        />
      )}

      {loading && <p className="text-sm text-gray-500" role="status">Loading questions…</p>}
      {error && <p className="text-sm text-red-700">{error}</p>}
      {!loading && !error && threads.length === 0 && (
        <p className="text-sm text-gray-500">{emptyText || 'No questions have been asked yet.'}</p>
      )}

      <ul className="space-y-2">
        {threads.map((t) => {
          const expanded = openId === t.id;
          return (
            <li key={t.id} className="border border-gray-200 rounded-lg bg-white">
              <button
                type="button"
                onClick={() => setOpenId(expanded ? null : t.id)}
                aria-expanded={expanded}
                className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-gray-50 rounded-lg"
              >
                {t.unread && <span className="mt-1.5 w-2 h-2 shrink-0 rounded-full bg-stanford-red" aria-label="New activity" />}
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_PILL[t.status] || STATUS_PILL.closed}`}>
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                    {!isClaim && t.subjectType === 'claim' && (
                      <span className="text-xs text-gray-600 break-words">{t.subjectLabel}</span>
                    )}
                    {!isClaim && t.subjectType === 'report' && (
                      <span className="text-xs text-gray-600">The report</span>
                    )}
                  </span>
                  <span className="block mt-0.5 text-sm text-gray-900 break-words">{t.questionPreview}</span>
                  <span className="block text-xs text-gray-500">
                    {t.askedByName} → {t.directedToLabel} · {formatWhen(t.lastMessageAt)}
                  </span>
                </span>
              </button>
              {expanded && (
                <div className="px-3 pb-3">
                  <ClarificationThread threadId={t.id} showSubjectLink={!isClaim} onChanged={changed} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ClarificationPanel;
